import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, MousePointerClick, Users, Mail, ChevronDown, ChevronRight, BarChart3, Eye, Globe } from "lucide-react";

export default function AnalyticsPage() {
  const [campaigns, setCampaigns] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState({});
  const [geo, setGeo] = useState(null);
  const [visitors, setVisitors] = useState(null);

  useEffect(() => { api.get("/admin/campaigns").then((r) => setCampaigns(r.data)); }, []);
  useEffect(() => { api.get("/admin/analytics/geo").then((r) => setGeo(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/admin/analytics/visitors").then((r) => setVisitors(r.data)).catch(() => {}); }, []);

  const toggle = async (id) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id);
    if (!detail[id]) {
      const res = await api.get(`/admin/campaigns/${id}`);
      setDetail((d) => ({ ...d, [id]: res.data }));
    }
  };

  if (!campaigns) return <div className="flex items-center justify-center h-96"><Loader2 className="animate-spin text-zinc-400" /></div>;

  return (
    <div className="animate-fade-up max-w-4xl pb-16" data-testid="analytics-page">
      <span className="text-xs font-heading font-bold uppercase tracking-[0.2em] text-zinc-500">Marketing</span>
      <h1 className="font-heading font-light text-4xl tracking-tight mt-1 mb-2">Link analytics</h1>
      <p className="text-zinc-500 font-body mb-6">Track who clicked the links in your campaigns. Every mailer with a button is tracked automatically.</p>

      {visitors && visitors.total > 0 && (
        <div className="bg-white border border-zinc-200/80 rounded-xl p-6 mb-6" data-testid="visitors-analytics">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={16} className="text-zinc-400" />
            <h2 className="font-heading font-semibold">Website visitors</h2>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="rounded-xl bg-zinc-50 p-4" data-testid="visitors-total">
              <div className="text-3xl font-heading font-light">{visitors.total}</div>
              <div className="text-xs uppercase tracking-wider text-zinc-400 mt-1">Page views</div>
            </div>
            <div className="rounded-xl bg-zinc-50 p-4" data-testid="visitors-unique">
              <div className="text-3xl font-heading font-light">{visitors.unique}</div>
              <div className="text-xs uppercase tracking-wider text-zinc-400 mt-1">Unique visitors</div>
            </div>
          </div>
          <div className="text-xs font-heading font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">Visitors by country</div>
          <div className="space-y-2.5">
            {visitors.countries.map((c) => (
              <div key={c.country_code} className="flex items-center gap-3" data-testid={`visitor-geo-${c.country_code}`}>
                <span className="text-lg leading-none w-6">{c.flag}</span>
                <span className="text-sm font-body w-40 truncate">{c.country}</span>
                <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-black rounded-full" style={{ width: `${Math.max(6, (c.visits / visitors.total) * 100)}%` }} />
                </div>
                <span className="text-sm font-heading font-bold w-16 text-right">{c.visits} <span className="text-zinc-400 font-normal">/ {c.unique}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {geo && geo.total > 0 && (
        <div className="bg-white border border-zinc-200/80 rounded-xl p-6 mb-6" data-testid="geo-analytics">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={16} className="text-zinc-400" />
            <h2 className="font-heading font-semibold">Leads by country</h2>
            <span className="text-xs text-zinc-400">· {geo.total} located</span>
          </div>
          <div className="space-y-2.5">
            {geo.countries.map((c) => (
              <div key={c.country_code} className="flex items-center gap-3" data-testid={`geo-row-${c.country_code}`}>
                <span className="text-lg leading-none w-6">{c.flag}</span>
                <span className="text-sm font-body w-40 truncate">{c.country}</span>
                <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-black rounded-full" style={{ width: `${Math.max(6, (c.count / geo.total) * 100)}%` }} />
                </div>
                <span className="text-sm font-heading font-bold w-8 text-right">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {campaigns.length === 0 ? (
        <div className="bg-white border border-zinc-200/80 rounded-xl p-16 text-center">
          <BarChart3 className="mx-auto text-zinc-300" size={40} />
          <p className="text-zinc-400 font-body mt-4">No campaigns sent yet. Send one from the Mailer to start tracking clicks.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <div key={c.id} className="bg-white border border-zinc-200/80 rounded-xl overflow-hidden" data-testid={`campaign-${c.id}`}>
              <button onClick={() => toggle(c.id)} className="w-full flex items-center gap-4 p-5 text-left hover:bg-zinc-50" data-testid={`campaign-toggle-${c.id}`}>
                {open === c.id ? <ChevronDown size={18} className="text-zinc-400" /> : <ChevronRight size={18} className="text-zinc-400" />}
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold truncate">{c.subject}</div>
                  <div className="text-xs text-zinc-400 mt-0.5">{(c.created_at || "").slice(0, 10)} {c.tracked ? "· tracked" : "· no link"}</div>
                </div>
                <Stat icon={Mail} label="Sent" value={c.sent} />
                <Stat icon={Eye} label="Opens" value={c.unique_opens} />
                <Stat icon={MousePointerClick} label="Clicks" value={c.unique_clicks} />
              </button>
              {open === c.id && (
                <div className="border-t border-zinc-100 p-5" data-testid={`campaign-detail-${c.id}`}>
                  {!detail[c.id] ? (
                    <div className="flex justify-center py-4"><Loader2 className="animate-spin text-zinc-400" size={18} /></div>
                  ) : detail[c.id].recipients.length === 0 ? (
                    <p className="text-sm text-zinc-400 text-center py-2">No opens or clicks recorded yet.</p>
                  ) : (
                    <div className="border border-zinc-100 rounded-xl divide-y divide-zinc-100">
                      {detail[c.id].recipients.map((k) => (
                        <div key={k.email} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`clicker-${k.email}`}>
                          <span className="font-body">{k.email || "unknown"}</span>
                          <div className="flex items-center gap-3">
                            {k.opened && <span className="text-xs text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5 flex items-center gap-1"><Eye size={11} /> opened</span>}
                            {k.clicks > 0 && <span className="text-xs text-blue-600 bg-blue-50 rounded-full px-2 py-0.5 flex items-center gap-1"><MousePointerClick size={11} /> {k.clicks} click{k.clicks === 1 ? "" : "s"}</span>}
                            <span className="text-xs text-zinc-400">{(k.last_activity || "").slice(0, 16).replace("T", " ")}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="text-center px-3 shrink-0">
    <div className="flex items-center justify-center gap-1 text-lg font-heading font-bold"><Icon size={14} className="text-zinc-400" /> {value ?? 0}</div>
    <div className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</div>
  </div>
);
